import React, { useContext, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { navigate } from '@reach/router';

// Context
import { AnalyticsContext } from '../analytics/AnalyticsContext';

// Hooks
import useSitePages from '../hooks/useSitePages/useSitePages';
import { useVisitId } from '../hooks/useVisitId';
import useContextToken from '../hooks/useContextToken';

// Components
import {
  Layout,
  FooterSectionCTA,
  HeroBanner,
  SeriesDetails,
  SEO,
  TabView,
  Seasons,
  ShopBannerCTA,
} from '../components';

// Utils
import { generateSeasons, getBuyflowLink } from '../utils/helpers';

// Models
import { IMedia, INetwork } from '../utils/models';

// Styles
import './media.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';

let tealiumTrackPageLoad;
if (typeof window !== 'undefined') {
  const { trackPageLoad } = initTealiumTracker({ debugMode: true });
  tealiumTrackPageLoad = trackPageLoad;
}

interface IProps {
  data: any;
  pageContext: {
    id: string;
    tmsId: string;
    network: INetwork;
  };
}

export const SERIES_QUERY = graphql`
  query ($id: ID!, $tmsId: String!) {
    allSOD {
      series(id: $id, cacheValue: false) {
        altIds {
          tmsSeriesId
        }
        ovpUrl
        title
        imageUrl
        heroImage {
          childImageSharp {
            gatsbyImageData(
              layout: FULL_WIDTH
              quality: 100
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
        descriptors {
          moods
        }
        description
        shortDescription
        ratings {
          rating
          agency
        }
        genres {
          name
          id
        }
        topCast {
          characterName
          name
          personId
          role
        }
        offer {
          providerName
          providerId
          offerType
          networkImgUrl
          startTime
          endTime
        }
        releaseDate
        totalSeasons
        episodes {
          id
          episodeTitle
          episodeNum
          seasonNum
          description
          seriesId
          seasonNum
          releaseDate
          offer {
            offerType
            providerId
            movieAssetId
            startTime
            endTime
          }
        }
      }

      # # Similar Assets
      similarAssets(id: $id, limit: "10") {
        ...SimilarAssetsFragment
      }
    }
    # # Access Services
    accessItem(tmsId: { eq: $tmsId }) {
      accessServices
      episodes {
        tmsId
        episodeNo
        seasonNo
        accessServices
      }
    }
  }
`;
const Series = ({ data, pageContext }: IProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const { sitePageMap } = useSitePages();
  const [relatedAssets, setRelatedAssets] = useState<IMedia[]>([]);
  const visitId = useVisitId();
  const { contextToken } = useContextToken();

  const buyflowLinkShopBanner = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_indvtvshow_shopspecplans_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_indvtvshow_shopspecplans_auth_050523'
  );
  const buyflowLinkShopFooter = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_indvtvshow_shop_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_indvtvshow_shop_auth_050523'
  );

  if (!data.allSOD.series || !data.allSOD.series.altIds) {
    console.log(
      `================NO SERIES DATA!!!!!!!!!================`,
      pageContext
    );
    console.log(`data: `, JSON.stringify(data, null, 2));
    navigate('/404');
    return <></>;
  }

  const {
    series,
    similarAssets: { similarAssets },
  } = data.allSOD;

  const seriesWithAccessServices = {
    ...series,
    accessServices: data.accessItem?.accessServices || [],
    tmsId: pageContext.tmsId,
  };

  const { altIds, title, description, shortDescription, episodes } = series;

  if (!episodes) {
    console.log(`================NO EPISODES!!!!!!!!!================`, {
      title,
      altIds,
      episodes,
    });
  }

  const episodesWithAccessServices = series.episodes.map((episode) => {
    const accessServices = data.accessItem?.episodes?.find(
      (item) => item.tmsId === episode.id
    )?.accessServices;
    return { ...episode, accessServices: accessServices || [] };
  });

  // TODO: Figure out why episodes is coming back as null sometimes
  const seasons =
    episodes &&
    generateSeasons(seriesWithAccessServices, episodesWithAccessServices);

  // SEO
  const { network } = pageContext;
  const { displayName } = network || {};

  const pageTitle = displayName ? `${title} | ${displayName}` : title;

  const pageSummary =
    shortDescription ||
    description ||
    `Watch ${title} with Spectrum On Demand.`;

  const pageNetwork = displayName ? ` Available on ${displayName}.` : '';

  const pageDescription = `${pageSummary}${pageNetwork}`;

  const header = (
    <HeroBanner trailer={null}>
      <HeroBanner.HeroImage
        // imageUrl={`${BASE_IMAGE_URI}/imageserver/series/${altIds.tmsSeriesId}`}
        heroImage={seriesWithAccessServices.heroImage}
        imageAltText={seriesWithAccessServices.title}
      />
      <HeroBanner.MediaContent
        media={seriesWithAccessServices}
        network={network}
        analytics={analytics}
      />
    </HeroBanner>
  );

  // For Related To Swimlane
  useEffect(() => {
    const assetsWithPages = similarAssets.reduce((acc, asset) => {
      // Only include assets that pages have been created for
      const path = sitePageMap?.get(asset.id);
      if (path) {
        acc = [...acc, { ...asset, path }];
      }
      return acc;
    }, []);

    setRelatedAssets(assetsWithPages);
  }, [sitePageMap, similarAssets]);

  // Quantum Tracking for page view
  useEffect(() => {
    if (analytics) {
      analytics.trackPageView(`Series Detail`, {
        currPageAppSection: 'Series',
        currPageIsLazyLoad: false,
      });
      analytics.trackPageView(`tvShows`, {
        currPageAppSection: 'tvShows',
        currPageIsLazyLoad: false,
      });
    }
  }, [analytics, seriesWithAccessServices]);

  // Tealium Page Tracking
  useEffect(() => {
    tealiumTrackPageLoad({
      pagename: 'Series Details Page',
      section: 'TV',
      pagetype: 'Series Page',
      sc_reporting_suite: 'charterspectrumondemand',
      TVSeries: title,
      contentId: parseInt(altIds.tmsSeriesId, 10),
    });
  }, [altIds, title]);

  return (
    <Layout className="media-page" header={header}>
      <SEO title={pageTitle} description={pageDescription} />

      <ShopBannerCTA
        title="Watch this title and more with Spectrum TV"
        linkPath={`${buyflowLinkShopBanner}&visitId=${visitId}${contextToken}`}
        tealiumConfig={{
          dataLinkType: 'shop-banner__primary-button',
          dataLinkName: `Shop Banner - Shop Spectrum's Plans`,
        }}
      />

      {seasons?.length ? (
        <TabView
          tabs={['About', 'Episodes']}
          panels={[
            <SeriesDetails
              key="details"
              media={seriesWithAccessServices}
              similarAssets={relatedAssets}
              analytics={analytics}
            />,
            <Seasons key="seasons" seasons={seasons} analytics={analytics} />,
          ]}
        />
      ) : (
        <section>
          <SeriesDetails
            media={seriesWithAccessServices}
            similarAssets={relatedAssets}
            analytics={analytics}
            accessServices={data.accessItem?.accessServices || []}
          />
        </section>
      )}

      <FooterSectionCTA
        shopLink={`${buyflowLinkShopFooter}&visitId=${visitId}${contextToken}`}
        signInLink={`https://watch.spectrum.net/?visitId=${visitId}${contextToken}`}
        analytics={analytics}
      />
    </Layout>
  );
};

export default Series;
